/**
 * Pure, dependency-free helpers for parsing & formatting email metadata.
 *
 * These are intentionally side-effect free so they can be unit-tested in
 * isolation and reused by any component in the InboxView module. Nothing in
 * here touches Firestore/RTDB or React.
 */

/**
 * Extracts a display name + bare address from a raw "From"/"To" header value.
 * Handles the common `Name <mail@host>` form as well as bare addresses.
 *
 * @param {string} raw
 * @returns {{ name: string, email: string, hasName: boolean }}
 */
export const parseAddress = (raw) => {
  // Take the first entry when given a list of addresses.
  if (Array.isArray(raw)) raw = raw[0];

  // Structured address form (e.g. Nodemailer's `{ name, address }`, which is
  // how outgoing/scheduled mail now stores `from`/`to`). Older mail stored a
  // plain string, so we still handle both.
  if (raw && typeof raw === 'object') {
    const name = (raw.name || '').trim();
    const email = (raw.address || raw.email || '').trim();
    if (name && email) return { name, email, hasName: true };
    if (email) return { name: email, email, hasName: false };
    if (name) return { name, email: '', hasName: true };
    return { name: 'Unknown', email: '', hasName: false };
  }

  if (!raw || typeof raw !== 'string') {
    return { name: 'Unknown', email: '', hasName: false };
  }

  const match = raw.match(/^(.*?)\s*<(.+?)>$/);
  if (match) {
    const name = match[1].replace(/^["']|["']$/g, '').trim();
    return {
      name: name || match[2].trim(),
      email: match[2].trim(),
      hasName: name.length > 0,
    };
  }

  if (raw.includes('@')) {
    return { name: raw.trim(), email: raw.trim(), hasName: false };
  }

  return { name: raw.trim(), email: '', hasName: true };
};

/**
 * Returns just the bare email address from any header-ish string.
 * @param {string} raw
 * @returns {string} lowercased address or ''
 */
export const extractEmail = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  const m = raw.match(/<([^>]+)>/) || raw.match(/([^\s@]+@[^\s@]+\.[^\s@]+)/);
  return m ? (m[1] || m[0]).trim().toLowerCase() : '';
};

/**
 * Parses a raw header value (string, array, or nodemailer-style object list)
 * into a flat array of lowercased addresses. Comma / semicolon separated
 * strings are supported. Duplicates are collapsed.
 *
 * Used for reply-all prefill: we take the original mail's `to` + `cc` and
 * turn them into a clean address list that can then be filtered against the
 * user's own mailboxes and the current sender.
 *
 * @param {string|string[]|object|object[]|null|undefined} raw
 * @returns {string[]} unique, lowercased addresses
 */
export const parseAddressList = (raw) => {
  const out = [];
  const seen = new Set();
  const push = (addr) => {
    const clean = (addr || '').trim().toLowerCase();
    if (!clean || seen.has(clean)) return;
    seen.add(clean);
    out.push(clean);
  };
  const eat = (value) => {
    if (value == null) return;
    if (Array.isArray(value)) { value.forEach(eat); return; }
    if (typeof value === 'object') {
      const addr = (value.address || value.email || '').trim();
      if (addr) push(addr);
      return;
    }
    if (typeof value !== 'string') return;
    // Nodemailer / mailparser text form: "Name <a@x>, other@y". Split on
    // commas AND semicolons — some clients use `;` (Outlook history).
    value.split(/[,;]+/).forEach((chunk) => {
      const email = extractEmail(chunk) || (chunk.includes('@') ? chunk.trim().toLowerCase() : '');
      if (email) push(email);
    });
  };
  eat(raw);
  return out;
};

/**
 * Shortens an email address for tight UI spots (e.g. `julian.propst@…` → `julian.p…@host`).
 * @param {string} email
 * @param {number} max
 * @returns {string}
 */
export const shortenEmail = (email, max = 22) => {
  if (!email || email.length <= max) return email || '';
  const [user, domain] = email.split('@');
  if (!domain) return email;
  const shortUser = user.length > 10 ? `${user.slice(0, 8)}…` : user;
  return `${shortUser}@${domain}`;
};

/**
 * Up to two uppercase initials for an avatar bubble.
 * @param {string} nameOrEmail
 * @returns {string}
 */
export const getInitials = (nameOrEmail) => {
  if (!nameOrEmail) return '?';
  const cleaned = nameOrEmail.replace(/[<>"]/g, '').trim();
  const base = cleaned.includes('@') ? cleaned.split('@')[0] : cleaned;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

/**
 * Deterministic avatar background color derived from a string.
 *
 * Stays within the BonusClub blue brand family (hue ≈ 198–220) and only varies
 * lightness/saturation slightly, so avatars look cohesive instead of a random
 * rainbow while remaining distinguishable per sender.
 *
 * @param {string} seed
 * @returns {string} hsl color
 */
export const avatarColor = (seed) => {
  const s = seed || 'x';
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  const abs = Math.abs(hash);
  const hue = 198 + (abs % 22);        // 198–219 → brand blue range
  const sat = 38 + (abs % 14);         // 38–51%
  const light = 46 + ((abs >> 3) % 16); // 46–61%
  return `hsl(${hue}, ${sat}%, ${light}%)`;
};

/**
 * Normalizes any timestamp-ish value (ms number, ISO string, Firestore Timestamp)
 * into a JS Date or null.
 */
export const toDate = (value) => {
  if (value == null) return null;
  if (typeof value === 'object' && typeof value.toMillis === 'function') {
    return new Date(value.toMillis());
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6));
  }
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? new Date(ms) : null;
};

/**
 * Epoch-ms for any timestamp-ish value, or null when it can't be parsed.
 */
export const toMillis = (value) => {
  const date = toDate(value);
  return date ? date.getTime() : null;
};

/**
 * Actual planned send time for a scheduled-mail doc.
 *
 * Prefer `scheduledAt` (the time after sleep/weekend adjustment) over the
 * original pre-adjustment stamp. Older docs only have one of these fields.
 */
export const getScheduledSendAt = (email) => {
  if (!email) return null;
  return toDate(email.scheduledAt)
    || toDate(email.scheduledTime)
    || toDate(email.originalScheduledTime)
    || toDate(email.timestamp)
    || toDate(email.date);
};

/**
 * When the user queued the scheduled mail — not when it will send.
 *
 * `queuedAt` is written at schedule time. Fallbacks are other created-at
 * stamps; send-time fields are intentionally ignored so a mail queued
 * 10 minutes ago still sorts above one queued yesterday that sends sooner.
 */
export const getScheduledQueuedAt = (email) => {
  if (!email) return null;
  return toDate(email.queuedAt)
    || toDate(email.createdAt)
    || toDate(email.timestamp)
    || toDate(email.date);
};

/**
 * The user's own mailbox a scheduled / outgoing mail will send from.
 * Scheduled docs store this as `senderEmail` (and sometimes a nodemailer
 * `{ name, address }` on `from`); incoming mail uses `accountEmail`.
 */
export const getOutgoingMailbox = (email) => {
  if (!email) return '';
  const direct = String(email.accountEmail || email.senderEmail || '').trim();
  if (direct) return direct;
  return parseAddress(email.from).email || '';
};

/**
 * Compact send-time label for the scheduled list: "Today 14:32",
 * "Tomorrow 09:15", or "18 Sep, 14:32".
 */
export const formatScheduledWhen = (value) => {
  const date = toDate(value);
  if (!date) return '';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfDate - startOfToday) / 86400000);
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (diffDays === 0) return `Today ${time}`;
  if (diffDays === 1) return `Tomorrow ${time}`;
  if (diffDays === -1) return `Yesterday ${time}`;
  return date.toLocaleString([], {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Most recently queued first. Missing queue stamps sink to the bottom so a
 * broken doc never jumps a mail the user just scheduled.
 */
export const sortScheduledNewestFirst = (rows) => [...rows].sort((a, b) => {
  const am = toMillis(getScheduledQueuedAt(a));
  const bm = toMillis(getScheduledQueuedAt(b));
  if (am == null && bm == null) return String(a?.id || '').localeCompare(String(b?.id || ''));
  if (am == null) return 1;
  if (bm == null) return -1;
  if (am !== bm) return bm - am;
  return String(a?.id || '').localeCompare(String(b?.id || ''));
});

/**
 * Mail-client style relative date: "09:42" today, "Yesterday", "Mon", or "12/05/2026".
 * @param {*} value timestamp-ish
 * @returns {string}
 */
export const formatRelativeDate = (value) => {
  const date = toDate(value);
  if (!date) return '';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday - startOfDate) / 86400000);

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  return date.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Full, human-readable date for the detail header.
 * @param {*} value timestamp-ish
 * @returns {string}
 */
export const formatFullDate = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleString([], {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Returns a best-effort plain-text preview for a mail (strips HTML tags).
 * @param {object} email
 * @returns {string}
 */
export const getPreviewText = (email) => {
  if (!email) return '';
  const raw = email.preview || email.bodyText || email.text || email.content || '';
  return raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};
